import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { subDays } from "date-fns";
import "./history-page";
import type {
  LogEntry,
  ExerciseType,
  ExerciseIntensity,
  CgmReading,
} from "../types";

// Generate sample data for a moderately well-controlled T1D
function generateSampleData(): LogEntry[] {
  const entries: LogEntry[] = [];
  const now = new Date();

  // Generate 35 days of data (supports 30-day view)
  for (let dayOffset = 0; dayOffset < 35; dayOffset++) {
    const date = subDays(now, dayOffset);

    // Sick days spread across the period
    const isSickDay = dayOffset === 12 || dayOffset === 26;
    const wasRecoverDay = dayOffset === 11 || dayOffset === 25;

    // Morning sick entry on day 3
    if (isSickDay) {
      const sickTime = new Date(date);
      sickTime.setHours(6, 30, 0, 0);
      entries.push({
        id: `sample-sick-${dayOffset}`,
        timestamp: sickTime,
        periodTag: "B",
        bloodGlucose: 14.2,
        ketones: 0.8,
        sickDay: { status: "sick", symptoms: "Cold/flu - feeling rough" },
        notes: "Woke up feeling unwell, slight fever",
        createdAt: sickTime,
        updatedAt: sickTime,
      });
    }

    // Recovery entry on day 2 (feeling better)
    if (wasRecoverDay) {
      const betterTime = new Date(date);
      betterTime.setHours(8, 0, 0, 0);
      entries.push({
        id: `sample-better-${dayOffset}`,
        timestamp: betterTime,
        periodTag: "B",
        bloodGlucose: 7.1,
        sickDay: { status: "better" },
        notes: "Feeling much better today",
        createdAt: betterTime,
        updatedAt: betterTime,
      });
    }

    // Exercise entries every 2-3 days (not on sick days)
    if (!isSickDay && dayOffset % 3 !== 2) {
      const exerciseTypes: ExerciseType[] = ["run", "walk", "cycle", "gym"];
      const exerciseType = exerciseTypes[dayOffset % exerciseTypes.length];
      const intensities: ExerciseIntensity[] = [
        "moderate",
        "light",
        "vigorous",
        "moderate",
      ];
      const intensity = intensities[dayOffset % intensities.length];
      const durations = [30, 45, 25, 40];
      const duration = durations[dayOffset % durations.length];

      const exTime = new Date(date);
      exTime.setHours(dayOffset === 0 ? 7 : 6, 15, 0, 0);

      entries.push({
        id: `sample-ex-${dayOffset}`,
        timestamp: exTime,
        periodTag: "B",
        bloodGlucose: 6.5 + Math.random() * 2,
        exercise: { type: exerciseType, intensity, duration },
        notes: dayOffset === 4 ? "Pre-breakfast exercise" : undefined,
        createdAt: exTime,
        updatedAt: exTime,
      });
    }

    // Breakfast - adjusted for sick day and exercise
    const bTime = new Date(date);
    bTime.setHours(7, 30 + Math.floor(Math.random() * 30), 0, 0);
    const breakfastBG = isSickDay ? 15.5 : 5.2 + Math.random() * 2.5;
    const breakfastCarbs = isSickDay ? 20 : 40 + Math.floor(Math.random() * 20);

    if (!isSickDay) {
      entries.push({
        id: `sample-b-${dayOffset}`,
        timestamp: bTime,
        periodTag: "B",
        bloodGlucose: breakfastBG,
        carbs: breakfastCarbs,
        qaBase: 5,
        qaCorrection:
          Math.random() > 0.7 ? Math.round((Math.random() * 2 - 1) * 2) / 2 : 0,
        createdAt: bTime,
        updatedAt: bTime,
      });
    }

    // Lunch
    const lTime = new Date(date);
    lTime.setHours(12, Math.floor(Math.random() * 30), 0, 0);
    const lunchBG = isSickDay
      ? 13.0 + Math.random() * 3
      : 5.0 + Math.random() * 3;
    const lunchCarbs = isSickDay ? 30 : 50 + Math.floor(Math.random() * 25);

    entries.push({
      id: `sample-l-${dayOffset}`,
      timestamp: lTime,
      periodTag: "L",
      bloodGlucose: lunchBG,
      carbs: lunchCarbs,
      qaBase: isSickDay ? 4 : 6,
      qaCorrection: isSickDay ? 2 : lunchBG > 8 ? 1 : 0,
      createdAt: lTime,
      updatedAt: lTime,
    });

    // Evening meal
    const emTime = new Date(date);
    emTime.setHours(18, 30 + Math.floor(Math.random() * 30), 0, 0);
    const eveningHigh = Math.random() > 0.8;

    entries.push({
      id: `sample-em-${dayOffset}`,
      timestamp: emTime,
      periodTag: "EM",
      bloodGlucose: isSickDay
        ? 11.5 + Math.random() * 3
        : eveningHigh
          ? 11 + Math.random() * 2
          : 5.5 + Math.random() * 2,
      carbs: 60 + Math.floor(Math.random() * 40),
      qaBase: 7,
      qaCorrection: eveningHigh ? 2 : 0,
      biBase: 14, // Long-acting at dinner
      createdAt: emTime,
      updatedAt: emTime,
    });

    // Bedtime - BG check only
    const btTime = new Date(date);
    btTime.setHours(22, Math.floor(Math.random() * 30), 0, 0);
    const bedtimeLow = Math.random() > 0.85;

    entries.push({
      id: `sample-bt-${dayOffset}`,
      timestamp: btTime,
      periodTag: "BT",
      bloodGlucose: bedtimeLow
        ? 3.5 + Math.random() * 0.8
        : 5.5 + Math.random() * 2.5,
      carbs: bedtimeLow ? 15 : undefined,
      notes: bedtimeLow ? "Treated hypo with juice" : undefined,
      createdAt: btTime,
      updatedAt: btTime,
    });

    // Occasional 3AM check
    if (Math.random() > 0.6) {
      const nightTime = new Date(date);
      nightTime.setHours(3, Math.floor(Math.random() * 30), 0, 0);
      entries.push({
        id: `sample-3am-${dayOffset}`,
        timestamp: nightTime,
        periodTag: "3AM",
        bloodGlucose: 5 + Math.random() * 3,
        createdAt: nightTime,
        updatedAt: nightTime,
      });
    }
  }

  return entries.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}

// Generate sample CGM readings for 35 days (supports 30-day view)
function generateSampleCgmData(): CgmReading[] {
  const readings: CgmReading[] = [];
  const now = new Date();

  // Days that will have hypo episodes (spread across 30 days)
  const hypoDays = [2, 5, 8, 10, 14, 17, 20, 23, 25, 28, 31];
  // Days that will have severe extended hypos
  const severeHypoDays = [8, 20];

  for (let dayOffset = 0; dayOffset < 35; dayOffset++) {
    const date = subDays(now, dayOffset);
    const isSickDay = dayOffset === 12 || dayOffset === 26;
    const isHypoDay = hypoDays.includes(dayOffset);
    const isSevereHypoDay = severeHypoDays.includes(dayOffset);

    // Generate 96 readings per day (every 15 min)
    for (let interval = 0; interval < 96; interval++) {
      const timestamp = new Date(date);
      timestamp.setHours(0, interval * 15, 0, 0);

      // Skip readings from today that would be in the future
      if (timestamp > now) continue;

      const hour = timestamp.getHours();

      // Base glucose varies by time of day
      let baseGlucose = 6.0;

      // Post-meal spikes
      if (hour >= 8 && hour < 10) baseGlucose = 8.5;
      if (hour >= 13 && hour < 15) baseGlucose = 8.0;
      if (hour >= 19 && hour < 21) baseGlucose = 9.0;

      // Early morning dawn phenomenon
      if (hour >= 4 && hour < 7) baseGlucose = 7.5;

      // Nighttime
      if (hour >= 0 && hour < 4) baseGlucose = 5.5;

      // Sick day - higher readings
      if (isSickDay) baseGlucose += 4.0;

      // Hypo episodes - create realistic hypo patterns
      if (isHypoDay) {
        // Night hypos (2-4 AM)
        if (hour >= 2 && hour < 4) {
          baseGlucose = isSevereHypoDay ? 2.8 : 3.5;
        }
        // Pre-lunch hypo (11-12 AM)
        if (hour >= 11 && hour < 12) {
          baseGlucose = 3.6;
        }
      }

      // Extended overnight hypo on severe days (1-6 AM)
      if (isSevereHypoDay && hour >= 1 && hour < 6) {
        baseGlucose = 3.0 + Math.random() * 0.5;
      }

      // Add realistic variation
      const variation = (Math.random() - 0.5) * 2.5;
      let glucose = baseGlucose + variation;

      // Clamp to realistic range
      glucose = Math.max(2.2, Math.min(22, glucose));

      readings.push({
        id: `cgm-sample-${timestamp.getTime()}`,
        timestamp,
        glucose: Math.round(glucose * 10) / 10,
        type: "historic",
        source: "libreview",
        deviceSerial: "SAMPLE123",
        createdAt: timestamp,
      });
    }
  }

  return readings.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
}

@customElement("sample-page")
export class SamplePage extends LitElement {
  @state() private sampleEntries: LogEntry[] = [];
  @state() private sampleReadings: CgmReading[] = [];

  static styles = css`
    :host {
      display: block;
    }

    .sample-banner {
      background: linear-gradient(135deg, #6366f1, #8b5cf6);
      color: white;
      padding: 0.75rem 1rem;
      text-align: center;
      font-size: 0.85rem;
    }

    .sample-banner strong {
      font-weight: 600;
    }

    history-page {
      display: block;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    // Generate fresh sample data on each visit
    this.sampleEntries = generateSampleData();
    this.sampleReadings = generateSampleCgmData();
  }

  render() {
    return html`
      <div class="sample-banner">
        <strong>📊 Sample Data Demo</strong> — This page demonstrates DAFNE
        pattern analysis using simulated data
      </div>
      <history-page
        .externalReadings=${this.sampleReadings}
        .externalEntries=${this.sampleEntries}
        pageTitle="Sample History Demo"
        pageSubtitle="Demo data showing DAFNE pattern analysis capabilities"
      ></history-page>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sample-page": SamplePage;
  }
}
